// Developer Platform — Marketplace tab: browse the offered integration connectors
// (migration-service /api/v1/integrations/marketplace), see the verified badge,
// install count and reviews, install one (creates a connection via the connector's
// config/credential schema), and leave a review. Mirrors the IntegrationsTab load +
// rollout/entitlement states and react-hot-toast conventions.
import React, { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage, isEntitlementUnavailable } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Download, Loader2, ShieldCheck, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import {
  createIntegrationConnection,
  getMarketplace,
  getMarketplaceReviews,
  isNotEntitled,
  isNotRolledOut,
  postMarketplaceReview,
  type ConnectorReview,
  type MarketplaceEntry,
  type SchemaField,
} from '@/services/developerService';
import { ActionRefusal } from '@/features/shared/components/ui/ActionRefusal';
import { errMsg, fmtDateTime, FeatureNotEnabledState, RolloutPendingState, TemporarilyUnavailableState } from './common';

const Stars: React.FC<{ value: number; onChange?: (n: number) => void; size?: number }> = ({ value, onChange, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        width={size}
        height={size}
        className={
          (n <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground') +
          (onChange ? ' cursor-pointer' : '')
        }
        onClick={onChange ? () => onChange(n) : undefined}
      />
    ))}
  </span>
);

export const InstallDialog: React.FC<{ entry: MarketplaceEntry; onClose: () => void; onInstalled: () => void }> = ({
  entry, onClose, onInstalled,
}) => {
  const { t } = useTranslation('admin');
  const [, setSearchParams] = useSearchParams();
  const [name, setName] = useState(entry.name);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [installing, setInstalling] = useState(false);
  const [reviews, setReviews] = useState<ConnectorReview[] | null>(null);
  const [myRating, setMyRating] = useState(entry.my_rating || 0);
  const [comment, setComment] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const configFields: SchemaField[] = entry.config_schema?.fields ?? [];
  const credFields: SchemaField[] = entry.credential_schema?.fields ?? [];

  useEffect(() => {
    let alive = true;
    getMarketplaceReviews(entry.key).then((r) => alive && setReviews(r)).catch(() => alive && setReviews([]));
    return () => { alive = false; };
  }, [entry.key]);

  const [refusal, setRefusal] = useState<string | null>(null);
  const set = (k: string, v: string) => { setRefusal(null); setVals((s) => ({ ...s, [k]: v })); };

  const install = async () => {
    // Required-field check.
    for (const f of [...configFields, ...credFields]) {
      if (f.required && !vals[f.key]?.trim()) {
        toast.error(t('developer.mkt_field_required', { defaultValue: '{{field}} is required', field: f.label || f.key }));
        return;
      }
    }
    const config: Record<string, string> = {};
    const credentials: Record<string, string> = {};
    configFields.forEach((f) => { if (vals[f.key]) config[f.key] = vals[f.key]; });
    credFields.forEach((f) => { if (vals[f.key]) credentials[f.key] = vals[f.key]; });
    if (entry.target_key && !config.target_key) config.target_key = entry.target_key;

    setInstalling(true);
    setRefusal(null);
    try {
      await createIntegrationConnection({
        name: name.trim() || entry.name,
        connector_key: entry.key,
        config,
        credentials,
      });
      toast.success(t('developer.mkt_installed', { defaultValue: 'Installed — find it under Integrations.' }));
      onInstalled();
      onClose();
    } catch (e) {
      // Kept on the dialog (role=alert) until the user edits or retries — e.g. an
      // internal SFTP host / source URL the server refuses.
      setRefusal(getApiErrorMessage(e, errMsg(e, t('developer.mkt_install_failed', { defaultValue: 'Could not install this connector.' }))));
    } finally {
      setInstalling(false);
    }
  };

  const submitReview = async () => {
    if (myRating < 1) {
      toast.error(t('developer.mkt_pick_rating', { defaultValue: 'Pick a rating first.' }));
      return;
    }
    setReviewing(true);
    try {
      await postMarketplaceReview(entry.key, myRating, comment);
      setComment('');
      setReviews(await getMarketplaceReviews(entry.key));
      toast.success(t('developer.mkt_review_saved', { defaultValue: 'Thanks — your review was saved.' }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.mkt_review_failed', { defaultValue: 'Could not save your review.' }))));
    } finally {
      setReviewing(false);
    }
  };

  const field = (f: SchemaField) => (
    <div key={f.key} className="space-y-1">
      <Label className="text-xs">
        {f.label || f.key}{f.required ? ' *' : ''}
      </Label>
      <Input
        type={f.type === 'secret' ? 'password' : 'text'}
        autoComplete={f.type === 'secret' ? 'new-password' : 'off'}
        value={vals[f.key] || ''}
        onChange={(e) => set(f.key, e.target.value)}
        placeholder={f.type === 'secret' ? '••••••••' : ''}
      />
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {entry.name}
            {entry.verified && <Badge className="bg-emerald-100 text-emerald-700 gap-1"><ShieldCheck className="w-3 h-3" /> Verified</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary">{entry.category}</Badge>
          <Badge variant="outline">{entry.direction}</Badge>
          <Badge variant="outline">{entry.transport}</Badge>
          <span className="inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> {entry.install_count} installs</span>
          {entry.avg_rating != null && (
            <span className="inline-flex items-center gap-1"><Stars value={entry.avg_rating} /> {entry.avg_rating.toFixed(1)} ({entry.review_count})</span>
          )}
          {entry.docs_url && <a href={entry.docs_url} target="_blank" rel="noreferrer" className="text-primary-500 underline">Docs</a>}
        </div>

        {/* OAuth-based connectors (Asana) never use the generic install form:
            they connect through their own card on the Integrations tab. */}
        {entry.key === 'asana' ? (
          <div className="mt-3 space-y-3 rounded-md border p-3">
            <div className="font-medium text-sm">{t('developer.mkt_oauth_title', { defaultValue: 'Connects with OAuth' })}</div>
            <p className="text-sm text-muted-foreground">
              {t('developer.mkt_oauth_hint', {
                defaultValue: 'Asana is set up by signing in to your Asana account — no credentials to paste. Use the Asana card on the Integrations tab.',
              })}
            </p>
            <Button
              onClick={() => {
                onClose();
                // SPA navigation: the app lives on tenant-prefixed paths, so
                // only the ?tab= query changes (DeveloperPage reads it live).
                setSearchParams({ tab: 'integrations' }, { replace: true });
              }}
            >
              {t('developer.mkt_oauth_goto', { defaultValue: 'Set up on the Integrations tab' })}
            </Button>
          </div>
        ) : (
        <div className="mt-3 space-y-3 rounded-md border p-3">
          <div className="font-medium text-sm">{t('developer.mkt_install', { defaultValue: 'Install' })}</div>
          <div className="space-y-1">
            <Label className="text-xs">{t('developer.mkt_conn_name', { defaultValue: 'Connection name' })}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {configFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{configFields.map(field)}</div>
          )}
          {credFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{credFields.map(field)}</div>
          )}
          {configFields.length === 0 && credFields.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('developer.mkt_no_config', { defaultValue: 'No configuration needed — install and manage it under Integrations.' })}
            </p>
          )}
        </div>
        )}

        {/* Reviews */}
        <div className="mt-2 space-y-3">
          <div className="font-medium text-sm">{t('developer.mkt_reviews', { defaultValue: 'Reviews' })}</div>
          <div className="flex items-center gap-2">
            <Stars value={myRating} onChange={setMyRating} size={20} />
            <Input
              className="flex-1"
              placeholder={t('developer.mkt_review_ph', { defaultValue: 'Share how this connector works for you (optional)' })}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Button size="sm" onClick={submitReview} disabled={reviewing}>
              {reviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : t('developer.mkt_review_submit', { defaultValue: 'Rate' })}
            </Button>
          </div>
          {reviews == null ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : reviews.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('developer.mkt_no_reviews', { defaultValue: 'No reviews yet — be the first.' })}</p>
          ) : (
            <div className="space-y-2">
              {reviews.map((r) => (
                <div key={r.id} className="rounded border p-2">
                  <div className="flex items-center justify-between">
                    <Stars value={r.rating} />
                    <span className="text-xs text-muted-foreground">{fmtDateTime(r.updated_at)}</span>
                  </div>
                  {r.comment && <p className="text-sm mt-1">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        <ActionRefusal message={refusal} onDismiss={() => setRefusal(null)} testId="marketplace-install-refusal" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t('developer.cancel', { defaultValue: 'Cancel' })}</Button>
          {entry.key !== 'asana' && (
            <Button onClick={install} disabled={installing}>
              {installing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              {t('developer.mkt_install_btn', { defaultValue: 'Install connector' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MarketplaceTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [entries, setEntries] = useState<MarketplaceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rolloutPending, setRolloutPending] = useState(false);
  const [notEnabledMsg, setNotEnabledMsg] = useState<string | null>(null);
  const [unavailableMsg, setUnavailableMsg] = useState<string | null>(null);
  const [selected, setSelected] = useState<MarketplaceEntry | null>(null);

  const load = () => {
    let alive = true;
    setLoading(true);
    getMarketplace()
      .then((e) => { if (alive) setEntries(e); })
      .catch((e) => {
        if (!alive) return;
        if (isEntitlementUnavailable(e)) setUnavailableMsg(errMsg(e, ''));
        else if (isNotEntitled(e)) setNotEnabledMsg(errMsg(e, ''));
        else if (isNotRolledOut(e)) setRolloutPending(true);
        else setLoadError(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };
  useEffect(load, []);

  const verifiedCount = useMemo(() => entries.filter((e) => e.verified).length, [entries]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (unavailableMsg !== null) return <TemporarilyUnavailableState message={unavailableMsg} />;
  if (notEnabledMsg !== null) return <FeatureNotEnabledState message={notEnabledMsg} />;
  if (rolloutPending) return <RolloutPendingState message={t('developer.mkt_rollout', { defaultValue: 'The connector marketplace is being rolled out.' })} />;
  if (loadError) return <p className="text-sm text-danger py-6">{t('developer.mkt_load_failed', { defaultValue: 'Could not load the marketplace.' })}</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('developer.mkt_intro', {
          defaultValue: 'Browse and install integration connectors. {{verified}} of {{total}} are KaziPeople-verified.',
          verified: verifiedCount, total: entries.length,
        })}
      </p>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6">{t('developer.mkt_empty', { defaultValue: 'No connectors are available yet.' })}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {entries.map((e) => (
            <Card key={e.key} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium leading-tight">{e.name}</div>
                  {e.verified && (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1 shrink-0">
                      <ShieldCheck className="w-3 h-3" /> {t('developer.mkt_verified', { defaultValue: 'Verified' })}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="secondary">{e.category}</Badge>
                  <Badge variant="outline">{e.direction}</Badge>
                  <Badge variant="outline">{e.transport}</Badge>
                  {e.status === 'beta' && <Badge className="bg-amber-100 text-amber-700">beta</Badge>}
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> {e.install_count}</span>
                  {e.avg_rating != null ? (
                    <span className="inline-flex items-center gap-1"><Stars value={e.avg_rating} /> {e.avg_rating.toFixed(1)}</span>
                  ) : (
                    <span className="text-xs">{t('developer.mkt_no_ratings', { defaultValue: 'No ratings' })}</span>
                  )}
                </div>
                <Button size="sm" className="w-full" onClick={() => setSelected(e)}>
                  {e.my_rating ? <CheckCircle2 className="w-4 h-4 mr-1.5" /> : null}
                  {t('developer.mkt_view', { defaultValue: 'View & install' })}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {selected && (
        <InstallDialog entry={selected} onClose={() => setSelected(null)} onInstalled={load} />
      )}
    </div>
  );
};

export default MarketplaceTab;
